// Generated by GitHub Copilot
/**
 * NewRelic tracking utilities for problem view analytics
 * Implements custom page view tracking, unique visitor identification, and engagement metrics
 *
 * Enhanced with user profile integration (US1772925) to include logged-in user information
 * in NewRelic custom events for improved analytics and troubleshooting capabilities.
 *
 * @fileoverview All tracking functions accept optional userProfile parameter containing
 * user authentication data (name, email, title) which is included in event payloads
 * when available, while maintaining full backward compatibility for anonymous users.
 *
 * NOTE: As of US1915192, user identity is set globally via window.newrelic.setUserId()
 * and setCustomAttribute() in AppWrapper.js. The userProfile parameter pattern below
 * is legacy — new tracking functions should not add userProfile parameters.
 *
 * ## Event Data Structures
 *
 * ### ProblemView Event
 * ```javascript
 * {
 *   eventType: "ProblemView",
 *   problemId: "PRBT12345",
 *   permanentNumber: "PRBT12345",
 *   userId: "user_1234567890_abc123def",
 *   isHistoricalVersion: false,
 *   timestamp: "2025-10-08T17:30:00.000Z",
 *   sessionId: "session_1234567890_xyz789",
 *   pageUrl: "https://app.example.com/problem/view/PRBT12345",
 *   referrer: "https://app.example.com/problems",
 *   // User profile fields (when authenticated)
 *   userName: "John Doe",
 *   userEmail: "john.doe@company.com",
 *   userTitle: "Senior Engineer",
 *   isAuthenticated: true
 * }
 * ```
 *
 * ### UniqueVisitor Event
 * ```javascript
 * {
 *   eventType: "UniqueVisitor",
 *   problemId: "PRBT12345",
 *   userId: "user_1234567890_abc123def",
 *   date: "2025-10-08",
 *   timestamp: "2025-10-08T17:30:00.000Z",
 *   // User profile fields (when authenticated)
 *   userName: "Jane Smith",
 *   userEmail: "jane.smith@company.com",
 *   userTitle: "Product Manager",
 *   isAuthenticated: true
 * }
 * ```
 *
 * ### ProblemEngagement Event
 * ```javascript
 * {
 *   eventType: "ProblemEngagement",
 *   problemId: "PRBT12345",
 *   userId: "user_1234567890_abc123def",
 *   totalTimeMs: 45000,
 *   totalTimeSeconds: 45,
 *   sessionId: "session_1234567890_xyz789",
 *   timestamp: "2025-10-08T17:30:45.000Z",
 *   // User profile fields (when authenticated)
 *   userName: "Bob Johnson",
 *   userEmail: "bob.johnson@company.com",
 *   userTitle: "DevOps Engineer",
 *   isAuthenticated: true
 * }
 * ```
 *
 * @author GitHub Copilot (US1772925)
 * @version 2.0.0 - Enhanced with user profile support
 */

/**
 * Generate or retrieve anonymous user identifier for tracking
 *
 * Creates a persistent anonymous identifier used for all NewRelic events, even when
 * user profile information is available. This allows correlation of anonymous and
 * authenticated sessions for the same user while maintaining privacy compliance.
 *
 * Uses localStorage to maintain consistent user identification across browser sessions.
 * Falls back to session-based identifiers when localStorage is unavailable.
 *
 * @returns {string} Anonymous user identifier (UUID-like string)
 * @example "user_1697654321000_abc123def"
 * @example "session_1697654321000_xyz789" (localStorage fallback)
 *
 * @since Enhanced in US1772925 - Works alongside user profile data for comprehensive tracking
 */
export const getAnonymousUserId = () => {
  const storageKey = "pwww_analytics_user_id";

  try {
    let userId = localStorage.getItem(storageKey);

    if (!userId) {
      // Generate a UUID-like identifier for anonymous tracking
      userId = `user_${Date.now()}_${Math.random()
        .toString(36)
        .substring(2, 11)}`;
      localStorage.setItem(storageKey, userId);
    }

    return userId;
  } catch (error) {
    // Fallback for browsers with localStorage disabled
    console.warn(
      "localStorage unavailable for analytics, using session-based ID"
    );
    return `session_${Date.now()}_${Math.random()
      .toString(36)
      .substring(2, 11)}`;
  }
};

/**
 * Track problem view with custom NewRelic page view name and user context
 *
 * Records a ProblemView custom event in NewRelic with comprehensive context including
 * problem details, user information (when available), and session data for analytics.
 *
 * @param {Object} newrelic - NewRelic browser agent instance with addPageAction method
 * @param {string} problemId - Problem identifier (e.g., "PRBT11225")
 * @param {string|null} permanentNumber - Problem permanent number if available
 * @param {boolean} isHistoricalVersion - Whether viewing a historical version
 * @param {Object|null} userProfile - User profile information for logged-in users
 * @param {string} userProfile.name - User's display name
 * @param {string} userProfile.email - User's email address
 * @param {string} userProfile.title - User's job title
 *
 * @example
 * // For logged-in user
 * trackProblemView(newrelic, "PRBT12345", null, false, {
 *   name: "John Doe",
 *   email: "john.doe@company.com",
 *   title: "Senior Engineer"
 * });
 *
 * @example
 * // For anonymous user (backward compatible)
 * trackProblemView(newrelic, "PRBT12345", null, false);
 *
 * @fires NewRelic#ProblemView - Custom event with problem and user context
 * @since US1772925 - Enhanced with user profile support
 */
export const trackProblemView = (
  newrelic,
  problemId,
  permanentNumber = null,
  isHistoricalVersion = false,
  userProfile = null
) => {
  if (!newrelic || !newrelic.addPageAction) {
    console.debug("NewRelic tracking: Agent not available or disabled");
    return;
  }

  if (!problemId) {
    console.warn("NewRelic tracking: Missing required parameters");
    return;
  }

  // Validate that required methods are available
  if (typeof newrelic.setPageViewName !== "function") {
    console.warn("NewRelic tracking: setPageViewName method not available");
    return;
  }

  if (typeof newrelic.addPageAction !== "function") {
    console.warn("NewRelic tracking: addPageAction method not available");
    return;
  }

  try {
    // Set custom page view name for better organization in NewRelic
    const pageViewName = isHistoricalVersion
      ? `Problem View (Historical): ${problemId}`
      : `Problem View: ${problemId}`;

    newrelic.setPageViewName(pageViewName);

    // Record custom event with problem context
    const userId = getAnonymousUserId();
    const eventData = {
      eventType: "ProblemView",
      problemId: problemId,
      permanentNumber: permanentNumber || problemId,
      userId: userId,
      uiType: "classic",
      isHistoricalVersion: isHistoricalVersion,
      timestamp: new Date().toISOString(),
      sessionId: getSessionId(),
      pageUrl: window.location.href,
      referrer: document.referrer || "direct",
    };

    // Generated by GitHub Copilot
    // Include user profile information if available (US1772925)
    if (userProfile && typeof userProfile === "object") {
      if (userProfile.name) eventData.userName = userProfile.name;
      if (userProfile.email) eventData.userEmail = userProfile.email;
      if (userProfile.title) eventData.userTitle = userProfile.title;
      eventData.isAuthenticated = true;
    } else {
      eventData.isAuthenticated = false;
    }

    newrelic.addPageAction("ProblemView", eventData);

    console.debug("NewRelic tracking: Problem view recorded", {
      problemId,
      isHistoricalVersion,
    });
  } catch (error) {
    console.error("NewRelic tracking error:", error);
  }
};

/**
 * Generate or retrieve session identifier
 *
 * Creates a session-scoped identifier for correlating NewRelic events within a single
 * browser session. Used alongside anonymous user IDs and user profile data to provide
 * comprehensive session analytics in NewRelic dashboards.
 *
 * Uses sessionStorage for session-scoped identification that persists across page
 * reloads but resets when browser session ends. Falls back to timestamp-based
 * identifiers when sessionStorage is unavailable.
 *
 * @returns {string} Session identifier
 * @example "session_1697654321000_abc123def"
 * @example "fallback_1697654321000_xyz789" (sessionStorage fallback)
 *
 * @private
 * @since Enhanced in US1772925 - Part of comprehensive user tracking system
 */
const getSessionId = () => {
  const storageKey = "pwww_analytics_session_id";

  try {
    let sessionId = sessionStorage.getItem(storageKey);

    if (!sessionId) {
      sessionId = `session_${Date.now()}_${Math.random()
        .toString(36)
        .substring(2, 11)}`;
      sessionStorage.setItem(storageKey, sessionId);
    }

    return sessionId;
  } catch (error) {
    // Fallback for browsers with sessionStorage disabled
    return `fallback_${Date.now()}_${Math.random()
      .toString(36)
      .substring(2, 11)}`;
  }
};

/**
 * Track unique visitor for a specific problem with user context
 *
 * Records a UniqueVisitor custom event in NewRelic once per day per problem per user.
 * Uses localStorage to prevent duplicate counting while including user profile data
 * for enhanced analytics and user behavior analysis.
 *
 * @param {Object} newrelic - NewRelic browser agent instance with addPageAction method
 * @param {string} problemId - Problem identifier for unique visitor tracking
 * @param {Object|null} userProfile - User profile information for logged-in users
 * @param {string} userProfile.name - User's display name
 * @param {string} userProfile.email - User's email address
 * @param {string} userProfile.title - User's job title
 *
 * @example
 * // Track unique visitor with user context
 * trackUniqueVisitor(newrelic, "PRBT12345", {
 *   name: "Jane Smith",
 *   email: "jane.smith@company.com",
 *   title: "Product Manager"
 * });
 *
 * @example
 * // Track anonymous unique visitor
 * trackUniqueVisitor(newrelic, "PRBT12345");
 *
 * @fires NewRelic#UniqueVisitor - Custom event with visitor and user context (daily)
 * @since US1772925 - Enhanced with user profile support
 */
export const trackUniqueVisitor = (newrelic, problemId, userProfile = null) => {
  if (!newrelic || !newrelic.addPageAction) {
    console.debug("NewRelic tracking: Agent not available or disabled");
    return;
  }

  if (!problemId) {
    return;
  }

  // Validate that required methods are available
  if (typeof newrelic.addPageAction !== "function") {
    console.warn(
      "NewRelic tracking: addPageAction method not available for unique visitor tracking"
    );
    return;
  }

  try {
    const today = new Date().toISOString().split("T")[0]; // YYYY-MM-DD format
    const storageKey = `pwww_unique_visitor_${problemId}_${today}`;
    const userId = getAnonymousUserId();

    // Check if this user has already been counted for this problem today
    const hasVisitedToday = localStorage.getItem(storageKey);

    if (!hasVisitedToday) {
      // Mark this user as having visited this problem today
      localStorage.setItem(storageKey, userId);

      // Record unique visitor event
      const uniqueVisitorData = {
        eventType: "UniqueVisitor",
        problemId: problemId,
        userId: userId,
        date: today,
        timestamp: new Date().toISOString(),
      };

      // Generated by GitHub Copilot
      // Include user profile information if available (US1772925)
      if (userProfile && typeof userProfile === "object") {
        if (userProfile.name) uniqueVisitorData.userName = userProfile.name;
        if (userProfile.email) uniqueVisitorData.userEmail = userProfile.email;
        if (userProfile.title) uniqueVisitorData.userTitle = userProfile.title;
        uniqueVisitorData.isAuthenticated = true;
      } else {
        uniqueVisitorData.isAuthenticated = false;
      }

      newrelic.addPageAction("UniqueVisitor", uniqueVisitorData);

      console.debug("NewRelic tracking: Unique visitor recorded", {
        problemId,
        userId,
        date: today,
      });
    }
  } catch (error) {
    console.error("NewRelic unique visitor tracking error:", error);
  }
};

/**
 * Initialize engagement tracking for a problem view with user context
 *
 * Sets up event listeners to track actual user engagement time on a problem page.
 * Records a ProblemEngagement custom event in NewRelic when the user leaves the page,
 * including total engagement time and user profile information for analysis.
 *
 * Automatically handles page visibility changes to accurately measure engagement time,
 * excluding time when the page is hidden or in background tabs.
 *
 * @param {Object} newrelic - NewRelic browser agent instance with addPageAction method
 * @param {string} problemId - Problem identifier for engagement tracking
 * @param {Object|null} userProfile - User profile information for logged-in users
 * @param {string} userProfile.name - User's display name
 * @param {string} userProfile.email - User's email address
 * @param {string} userProfile.title - User's job title
 *
 * @returns {Object} Engagement tracker object
 * @returns {Function} returns.cleanup - Call to remove event listeners and clean up
 *
 * @example
 * // Initialize engagement tracking with user context
 * const tracker = initializeEngagementTracking(newrelic, "PRBT12345", {
 *   name: "Bob Johnson",
 *   email: "bob.johnson@company.com",
 *   title: "DevOps Engineer"
 * });
 *
 * // Clean up when component unmounts
 * useEffect(() => {
 *   return () => tracker.cleanup();
 * }, [tracker]);
 *
 * @example
 * // Initialize anonymous engagement tracking
 * const tracker = initializeEngagementTracking(newrelic, "PRBT12345");
 *
 * @fires NewRelic#ProblemEngagement - Custom event with engagement time and user context
 * @listens document#visibilitychange - Tracks when page becomes hidden/visible
 * @listens window#beforeunload - Records final engagement time before page unload
 * @since US1772925 - Enhanced with user profile support
 */
export const initializeEngagementTracking = (
  newrelic,
  problemId,
  userProfile = null
) => {
  if (!newrelic || !newrelic.addPageAction) {
    console.debug("NewRelic tracking: Agent not available or disabled");
    return { cleanup: () => {} };
  }

  if (!problemId) {
    return { cleanup: () => {} };
  }

  // Validate that required methods are available
  if (typeof newrelic.addPageAction !== "function") {
    console.warn(
      "NewRelic tracking: addPageAction method not available for engagement tracking"
    );
    return { cleanup: () => {} };
  }

  const startTime = Date.now();
  const userId = getAnonymousUserId();
  let isVisible = !document.hidden;
  let totalEngagementTime = 0;
  let lastVisibilityChange = startTime;

  // Track visibility changes to calculate actual engagement time
  const handleVisibilityChange = () => {
    const now = Date.now();

    if (isVisible && document.hidden) {
      // Page became hidden, add to engagement time
      totalEngagementTime += now - lastVisibilityChange;
    } else if (!isVisible && !document.hidden) {
      // Page became visible, reset timer
      lastVisibilityChange = now;
    }

    isVisible = !document.hidden;
  };

  // Track page unload to capture final engagement time
  let hasFired = false;

  const handlePageUnload = () => {
    if (hasFired) return;
    hasFired = true;

    const now = Date.now();

    if (isVisible) {
      totalEngagementTime += now - lastVisibilityChange;
    }

    // Record engagement event
    const engagementData = {
      eventType: "ProblemEngagement",
      problemId: problemId,
      userId: userId,
      totalTimeMs: totalEngagementTime,
      totalTimeSeconds: Math.round(totalEngagementTime / 1000),
      sessionId: getSessionId(),
      timestamp: new Date().toISOString(),
    };

    // Generated by GitHub Copilot
    // Include user profile information if available (US1772925)
    if (userProfile && typeof userProfile === "object") {
      if (userProfile.name) engagementData.userName = userProfile.name;
      if (userProfile.email) engagementData.userEmail = userProfile.email;
      if (userProfile.title) engagementData.userTitle = userProfile.title;
      engagementData.isAuthenticated = true;
    } else {
      engagementData.isAuthenticated = false;
    }

    newrelic.addPageAction("ProblemEngagement", engagementData);

    console.debug("NewRelic tracking: Engagement recorded", {
      problemId,
      totalTimeSeconds: Math.round(totalEngagementTime / 1000),
    });
  };

  // Set up event listeners
  document.addEventListener("visibilitychange", handleVisibilityChange);
  window.addEventListener("beforeunload", handlePageUnload);

  // Return cleanup function
  return {
    cleanup: () => {
      handlePageUnload();
      document.removeEventListener("visibilitychange", handleVisibilityChange);
      window.removeEventListener("beforeunload", handlePageUnload);
    },
  };
};
